import { FadeUp } from "./Reveal";
import { NoteEyebrow } from "./NoteEyebrow";
import { EmSwash } from "./EmSwash";
import { SustainingBand } from "./SustainingBand";

/**
 * SustainingRoll — a quiet thank-you roll for monthly General Fund donors.
 * Names only (first name + last initial, or "Anonymous"), no amounts, no
 * photos. Three variants for founder review; `variant` = "roll" | "ledger" | "band".
 *
 * Data shape: [{ name: "Marcus T.", since: "Sep 2026", city?: "Oakland, CA" }]
 */
const Count = ({ n }) => (
  <p className="text-[13px] text-olive" data-testid="sustaining-count">
    <span className="font-serif text-3xl text-ink">{n}</span> sustaining {n === 1 ? "donor keeps" : "donors keep"} the fund ready year-round.
  </p>
);

const Header = ({ compact }) => (
  <div>
    <NoteEyebrow>Sustaining donors</NoteEyebrow>
    <h2 className={`mt-5 font-serif leading-[1.02] tracking-tight text-ink ${compact ? " text-3xl " : " "}`}>
      The ones who give <EmSwash>every month.</EmSwash>
    </h2>
    <p className="mt-4 max-w-xl text-base leading-relaxed text-olive">
      Between campaigns, these donors keep the General Fund ready so My Rich Aunt can say yes the day a verified student asks. Quietly, monthly, without being asked twice.
    </p>
  </div>
);

export const SustainingRoll = ({ donors = [], variant = "roll", testid = "sustaining-roll" }) => {
  if (variant === "ledger") {
    return (
      <section data-testid={testid} className="bg-sand">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-32">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5"><FadeUp><Header /><div className="mt-8"><Count n={donors.length} /></div></FadeUp></div>
            <FadeUp className="lg:col-span-6 lg:col-start-7">
              <ol className="divide-y divide-border border-y border-border">
                {donors.map((d, i) => (
                  <li key={i} className="flex items-baseline justify-between gap-6 py-3.5">
                    <span className="font-serif text-3xl text-ink">{d.name}</span>
                    <span className="shrink-0 text-[13px] uppercase tracking-[0.2em] text-olive">{d.city ? `${d.city} · ` : ""}since {d.since}</span>
                  </li>
                ))}
              </ol>
            </FadeUp>
          </div>
        </div>
      </section>
    );
  }

  if (variant === "band") {
    return <SustainingBand donors={donors} testid={testid} />;
  }

  return (
    <section data-testid={testid} className="relative bg-bone">
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-32">
        <FadeUp><Header /></FadeUp>
        <FadeUp className="mt-14">
          <ul className="flex flex-wrap gap-x-8 gap-y-4" data-testid="sustaining-roll-names">
            {donors.map((d, i) => (
              <li key={i} className="group">
                <span className="font-serif text-3xl text-ink">{d.name}</span>
                <span className="ml-2 align-middle text-[13px] uppercase tracking-[0.22em] text-olive/70 opacity-0 transition-opacity group-hover:opacity-100">since {d.since}</span>
              </li>
            ))}
          </ul>
        </FadeUp>
        <FadeUp className="mt-12 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <Count n={donors.length} />
          <p className="max-w-md text-[13px] leading-relaxed text-olive/80">Names appear only with the donor's permission at checkout. Amounts are never shown.</p>
        </FadeUp>
      </div>
    </section>
  );
};
